import { Toast } from "primereact/toast";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";
import { Column } from "primereact/column";
import { globalDataCol } from "../../appconfig/DataTableColumns";
import { getAllDetails, postAllData } from "../../service/ProductService";
import {
  GET_GLOBAL_LIST,
  UPDATE_GLOBAL_STATUS,
} from "../../config/ApiConstant";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";

const GlobalData = () => {
  // Set Toast
  const toast = useRef<Toast>(null);

  //Global data list state
  const [globalList, setGlobalList] = useState<any>([]);

  //Loading/Page Loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Confirm dialog for status change
  const [confirmStatusDialog, setConfirmStatusDialog] =
    useState<boolean>(false);
  const [statusChangeData, setStatusChangeData] = useState<{
    field: string;
    newStatus: boolean;
  } | null>(null);

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    is_transaction_wallet_enable: {
      value: null,
      matchMode: FilterMatchMode.EQUALS,
    },
    is_online_wallet_enable: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // useefect get global data list api
  useEffect(() => {
    getGlobalDataList();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // get global data list api
  const getGlobalDataList = async () => {
    try {
      const response = await getAllDetails(GET_GLOBAL_LIST);

      if (response) {
        setGlobalList([response]);
        setPageLoad(true);
      } else {
        setGlobalList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // Change status API call
  const changeGlobalStatus = async (field: string, newStatus: boolean) => {
    try {
      const payload = { [field]: newStatus };

      const response = await postAllData(UPDATE_GLOBAL_STATUS, payload);
      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        getGlobalDataList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Confirm status change
  const confirmChangeStatus = (field: string, newStatus: boolean) => {
    setStatusChangeData({ field, newStatus });
    setConfirmStatusDialog(true);
  };

  // Handle confirmed status change
  const handleConfirmStatusChange = () => {
    if (statusChangeData) {
      setLoading(true);
      changeGlobalStatus(statusChangeData.field, statusChangeData.newStatus);
      hideStatusDialog();
    }
  };

  // Hide confirm dialog
  const hideStatusDialog = () => {
    setConfirmStatusDialog(false);
    setStatusChangeData(null);
  };

  // Status switch template
  const statusTemplate = (rowData: any, field: string) => {
    const status = rowData[field];
    const statusClass = status
      ? "status status-active"
      : "status status-inactive";

    return (
      <div className="flex flex-column align-items-center">
        <InputSwitch
          checked={status}
          className="mb-2 mr-2"
          onChange={(e) => confirmChangeStatus(field, e.value)}
        />
        <span className={statusClass}>{status ? "Active" : "Inactive"}</span>
      </div>
    );
  };

  return (
    <>
      {/* Breadcrumb */}
      <AppBreadcrumb
        currentMenu="Setting"
        pageName="Global Data"
        pageTitle="Global Data"
      />
      <Toast ref={toast} />

      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            {/* Datatable */}
            {pageLoad ? (
              <>
                <DataTable
                  value={globalList}
                  showGridlines
                  stripedRows
                  rows={defaultRowOptions}
                  paginator={globalList.length >= 10 && true}
                  rowsPerPageOptions={defaultPageRowOptions}
                  className="datatable-responsive p-datatble-sm"
                  paginatorTemplate={paginatorLinks}
                  currentPageReportTemplate={showingEntries}
                  emptyMessage="No Data Found"
                  filters={filters}
                  onFilter={(e: any) => setFilters(e.filters)}
                  removableSort
                >
                  {globalDataCol.map((col, i) => {
                    if (col.field === "is_transaction_wallet_enable") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          body={(rowData) =>
                            statusTemplate(
                              rowData,
                              "is_transaction_wallet_enable"
                            )
                          }
                        />
                      );
                    } else if (col.field === "is_online_wallet_enable") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          body={(rowData) =>
                            statusTemplate(rowData, "is_online_wallet_enable")
                          }
                        />
                      );
                    } else {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                        />
                      );
                    }
                  })}
                </DataTable>
              </>
            ) : (
              <>
                {/* Skeleton Data table */}
                <DataTable value={Skeletonitems}>
                  {globalDataCol.map((col, i) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      body={SkeletonbodyTemplate}
                    />
                  ))}
                </DataTable>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirm status change dialog */}
      <Dialog
        visible={confirmStatusDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "450px" }}
        draggable={false}
        header="Change Global Status"
        modal
        footer={
          <>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={hideStatusDialog}
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              className="p-button-primary"
              onClick={handleConfirmStatusChange}
              loading={loading}
              disabled={loading}
            />
          </>
        }
        onHide={hideStatusDialog}
      >
        <div className="flex align-items-center justify-content-start">
          <i
            className="mr-3 pi pi-exclamation-triangle delete-triangle"
            style={{ fontSize: "2rem" }}
          />
          <span>
            Are you sure you want to set this{" "}
            <b>{statusChangeData?.field?.replace(/_/g, " ")}</b> as{" "}
            <b>{statusChangeData?.newStatus ? "Active" : "Inactive"}</b>?
          </span>
        </div>
      </Dialog>
    </>
  );
};

export default GlobalData;
